import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  addStudioScene,
  addStudioSceneBase,
  addVideoInstance,
  updateVideoInstance,
  deleteStudioImage,
  deleteStudioVideo,
  loadProject,
  loadProjectRequest,
  removeStudioScene,
  resetStudioCrudEvents,
  saveStudioResources,
  saveStudioResourcesFailure,
  saveStudioResourcesSuccess,
  setChangeTookPlace,
  setLoadedProject,
  saveScene,
  setSceneDescriptorValueByKey,
  setStudioLoading,
  setStudioResourceDelete,
  setStudioResourcePatch,
  setStudioResourcePost,
  saveShape,
  removeText,
  setShapeDescriptorAttribute,
  setProjectLabel,
  addLastStudioScene,
  setStudioError,
  deleteProject,
  deleteProjectSuccess,
  deleteProjectFailure,
  setCurrentStudioProjectID,
  setStudioCrudStateValue,
} from './studio.actions';
import { IdToIRI, IRIToId } from '../../../util/iri';
import {
  selectCurrentStudioProject,
  selectCurrentStudioScene,
  selectStudioCrudEvents,
  selectStudioScenes,
  selectStudioState,
} from './studio.selector';
import { EditorService } from '../../editor.service';
import { ResourceData } from '../../../elements/resource/resource.types';
import {
  StudioProject,
  StudioResourceType,
  StudioState,
} from './studio.reducer';
import { StudioScene, StudioImage, StudioVideo } from './studio.types';
import * as uuidv1 from 'uuid/v1';
import {
  setCurrentOrganisationID,
  setOrganisation,
} from '../../../organisation/organisation.actions';
import { Organisation } from '../../../organisation/organisation.interface';
import { selectCurrentOrganizationID } from '../../../organisation/organisation.selector';
import { StudioService } from './studio.service';
import { ErrorService } from '../../../services/error/error.service';

@Injectable()
export class StudioEffects {
  loadProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProject),
      withLatestFrom(this.store.select(selectCurrentStudioProject)),
      switchMap(([{ projectID }, project]) => {
        if (project?.id == projectID) {
          return [];
        }
        return [
          loadProjectRequest({ projectID }),
          setCurrentStudioProjectID({ projectID }),
          setStudioError({ value: undefined }),
          setStudioLoading({ value: true }),
          setStudioCrudStateValue({ key: 'index', value: 'loading' }),
        ];
      }),
    ),
  );

  loadProjectRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProjectRequest),
      switchMap(({ projectID }) => {
        return this.editorService.loadProjectOverview(projectID).pipe(
          switchMap(({ project, files, organisation }) => {
            const fileMap = files.reduce(
              (object, file) => ({
                ...object,
                [file.IRI]: file,
              }),
              {},
            ) as Record<string, ResourceData>;
            // -- // -- // -- //
            const org: Organisation = {
              id: IRIToId(organisation.IRI),
              name: organisation.literals.label,
              colorPalette: organisation.literals.descriptor?.colorPalette,
              fontFamily: (organisation.literals.descriptor as any)?.fontFamily,
            };
            return [
              setStudioCrudStateValue({ key: 'index', value: 'idle' }),
              setLoadedProject({ project, scenes: fileMap }),
              setOrganisation({ organisation: org }),
              setCurrentOrganisationID({ ID: org.id }),
              // the setting of studio loading to false is done in the component
            ];
          }),
          catchError(error => {
            console.error('loadProjectRequest error', error);
            return of(
              setStudioCrudStateValue({ key: 'index', value: 'error' }),
            );
          }),
        );
      }),
    ),
  );

  setProjectLabel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setProjectLabel),
      withLatestFrom(this.store.select(selectCurrentStudioProject)),
      switchMap(([{ label }, project]) => {
        return [
          setStudioResourcePatch({
            id: project.id,
            resourceType: 'project',
          }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  deleteProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteProject),
      withLatestFrom(this.store.select(selectCurrentOrganizationID)),
      switchMap(([{ projectID }, orgID]) => {
        // -- //
        return this.studioService.deleteProject(projectID, orgID).pipe(
          map(projectID => {
            return deleteProjectSuccess({ projectID });
          }),
          catchError(error => {
            this.errorService.logError(error);
            return of(
              deleteProjectFailure({
                projectID,
                error: error.message,
                code: error.status,
              }),
            );
          }),
        );
      }),
    ),
  );

  deleteImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteStudioImage),
      switchMap(({ imageId }) => {
        return [
          setStudioResourceDelete({
            id: imageId,
            resourceType: 'video',
          }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  deleteVideo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteStudioVideo),
      switchMap(({ videoId }) => {
        return [
          setStudioResourceDelete({
            id: videoId,
            resourceType: 'video',
          }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  setSceneDescriptorValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSceneDescriptorValueByKey),
      switchMap(({ sceneId }) => {
        return [
          setStudioResourcePatch({ id: sceneId, resourceType: 'scene' }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  setSceneDescriptor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveScene),
      withLatestFrom(this.store.select(selectCurrentStudioScene)),
      switchMap(([{ sceneId }, currentScene]) => {
        return [
          setStudioResourcePatch({
            id: sceneId || currentScene.id,
            resourceType: 'scene',
          }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  saveStudioResources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveStudioResources),
      withLatestFrom(
        this.store.select(selectStudioCrudEvents),
        this.store.select(selectStudioState),
        this.store.select(selectCurrentStudioProject),
      ),
      switchMap(([_action, crudEvents, studioState, project]) => {
        const { scenes, images, videos, projects } = studioState;

        // Convert posts to ResourceData (include project relationship for new files)
        const posts = this.convertToResourceData(
          crudEvents.posts,
          studioState,
          project?.id,
        );

        // Convert patches to ResourceData (no project relationship needed for patches)
        const patches = this.convertToResourceData(
          crudEvents.patches,
          studioState,
          project?.id,
        );

        // Convert deletes to ResourceData (only need IRI and type)
        const deletes = Object.entries(crudEvents.deletes).map(
          ([id, resourceType]) =>
            ({
              IRI: IdToIRI(id),
              type: this.getResourceType(resourceType),
            }) as ResourceData,
        );

        if (
          posts.length === 0 &&
          patches.length === 0 &&
          deletes.length === 0
        ) {
          return of({ type: '[Studio] No Changes' });
        }

        return this.editorService
          .saveResources({
            projectIRI: IdToIRI(project?.id),
            posts,
            patches,
            deletes,
          })
          .pipe(
            switchMap(() => [
              saveStudioResourcesSuccess(),
              resetStudioCrudEvents(),
            ]),
            catchError(error =>
              of(saveStudioResourcesFailure({ error: error.message })),
            ),
          );
      }),
    ),
  );

  setShapeDescriptorAttribute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setShapeDescriptorAttribute),
      withLatestFrom(this.store.select(selectCurrentStudioScene)),
      switchMap(([, currentScene]) => {
        console.log('setShapeDescriptorAttribute', currentScene);
        return [
          setStudioResourcePatch({
            id: currentScene.id,
            resourceType: 'scene',
          }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  saveShape$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveShape),
      withLatestFrom(this.store.select(selectCurrentStudioScene)),
      switchMap(([, currentScene]) => {
        return [
          setStudioResourcePatch({
            id: currentScene.id,
            resourceType: 'scene',
          }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  removeText$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeText),
      withLatestFrom(this.store.select(selectCurrentStudioScene)),
      switchMap(([, currentScene]) => {
        return [
          setStudioResourcePatch({
            id: currentScene.id,
            resourceType: 'scene',
          }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  removeScene$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeStudioScene),
      switchMap(({ sceneId }) => {
        return [
          setStudioResourceDelete({ id: sceneId, resourceType: 'scene' }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  addLastScene$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addLastStudioScene),
      withLatestFrom(this.store.select(selectStudioScenes)),
      switchMap(([, scenes]) => {
        // ----------- //

        const lastScene = scenes[scenes.length - 2];
        const atIndex = scenes.length - 1;
        const newId = uuidv1();
        const textInstanceId = uuidv1();
        const newScene: StudioScene = {
          id: newId,
          index: lastScene.index + 1,
          positionIndex: lastScene.positionIndex + 1,
          texts: [textInstanceId],
          shapes: {
            [textInstanceId]: {
              cell: {
                w: 1,
                h: 1,
                x: 0,
                y: 0,
                widthRatio: 0.6,
              },
              text: 'Hello World',
            },
          },
        };
        // const newId = uuidv1(); //
        return [
          addStudioSceneBase({ atIndex, scene: newScene }),
          setStudioResourcePost({ id: newId, resourceType: 'scene' }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  addStudioScene$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addStudioScene),
      withLatestFrom(
        this.store.select(selectStudioScenes),
        this.store.select(selectCurrentStudioScene),
      ),
      switchMap(([{ ref }, scenes, currentScene]) => {
        const newId = uuidv1();

        const atIndex = currentScene.positionIndex + (ref == 'after' ? 1 : 0);

        const scenesByPositionIndex = Object.values(scenes).sort(
          (a, b) => a.positionIndex - b.positionIndex,
        );

        console.log('addStudioScene', { atIndex, scenesByPositionIndex });

        const prev = scenesByPositionIndex.find(
          scene => scene.positionIndex == atIndex - 1,
        );
        const next = scenesByPositionIndex.find(
          scene => scene.positionIndex == atIndex,
        );

        let nextIndex: number;

        if (!prev) {
          nextIndex = next.index - 1;
        } else if (!next) {
          nextIndex = prev.index + 1;
        } else {
          nextIndex = (prev.index + next.index) / 2;
        }

        const textValue = `Scene ${atIndex + 1}`;
        const textInstanceId = uuidv1();

        const newScene: StudioScene = {
          id: newId,
          index: nextIndex,
          positionIndex: atIndex,
          texts: [textInstanceId],
          shapes: {
            [textInstanceId]: {
              cell: {
                w: 1,
                h: 1,
                x: 0,
                y: 0,
                widthRatio: 0.6,
              },
              text: textValue,
            },
          },
        };
        return [
          addStudioSceneBase({ atIndex, scene: newScene }),
          setStudioResourcePost({ id: newId, resourceType: 'scene' }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  addVideoInstance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addVideoInstance),
      switchMap(({ videoInstance }) => {
        const { mediaId: videoId } = videoInstance;
        return [
          setStudioResourcePatch({ id: videoId, resourceType: 'video' }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  updateVideoInstance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateVideoInstance),
      switchMap(({ videoId }) => {
        return [
          setStudioResourcePatch({ id: videoId, resourceType: 'video' }),
          setChangeTookPlace(),
        ];
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private editorService: EditorService,
    private studioService: StudioService,
    private readonly errorService: ErrorService,
  ) {}

  private convertToResourceData(
    crudItems: Record<string, StudioResourceType>,
    state: StudioState,
    projectID?: string,
  ): ResourceData[] {
    const { scenes, images, videos, projects } = state;
    return Object.entries(crudItems).map(([id, resourceType]) =>
      this.sceneToResourceData(
        resourceType,
        scenes[id] || images[id] || videos[id] || projects[id],
        projectID,
      ),
    );
  }

  private sceneToResourceData(
    type: 'scene' | 'image' | 'video' | 'project',
    scene: StudioScene | StudioImage | StudioVideo | StudioProject,
    projectID?: string,
  ): ResourceData {
    const descriptor: Record<string, any> = {
      type,
      ...scene,
    };

    const resource: ResourceData = {
      IRI: IdToIRI(scene.id),
      type: 'nw:File',
      literals: {
        label: (scene as StudioProject).name,
        descriptor,
      },
    };

    if (projectID) {
      resource.relationships = {
        project: IdToIRI(projectID),
      };
    }

    return resource;
  }

  private getResourceType(_type: StudioResourceType): string {
    // All studio resources are files
    return 'nw:File';
  }
}
